import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllClientLogs, getClientLogReport } from "../store/thunks/User";
import DataGrid, {
  Export,
  Selection,
  GroupPanel,
  Paging,
  Pager,
  Scrolling,
  ColumnFixing,
  SearchPanel,
  Column,
  HeaderFilter,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import { DateBox, Button } from "devextreme-react";
import { format } from "date-fns";
import "./TraderLogsPanel.css";

const TraderLogsPanel = ({ navValue }) => {
  const dispatch = useDispatch();
  const clientLogs = useSelector((state) => state.user.clientLogs || []);
  const isLoading = useSelector((state) => state.user.clientLogsLoading);
  const error = useSelector((state) => state.user.clientLogsError);
  const clientLogReport = useSelector((state) => state.user.clientLogReport);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredLogs, setFilteredLogs] = useState([]);

  // Fetch data based on selected tab
  useEffect(() => {
    if (navValue === 0) {
      dispatch(getAllClientLogs());
    } else if (navValue === 1) {
      dispatch(getClientLogReport());
    }
  }, [dispatch, navValue]);

  // Update filtered logs when clientLogs change or when search is performed
  useEffect(() => {
    let filtered = [...clientLogs];

    // Apply date filters if set
    if (startDate && endDate) {
      filtered = filtered.filter((log) => {
        const createdAt = new Date(log.createdAt);
        return createdAt >= startDate && createdAt <= endDate;
      });
    }

    setFilteredLogs(filtered);
  }, [clientLogs, startDate, endDate]);

  const handleClearSearch = async () => {
    setStartDate(null);
    setEndDate(null);
    await dispatch(getAllClientLogs());
  };

  const formatDate = (date) => {
    if (!date) return "";
    return format(new Date(date), "yyyy-MM-dd HH:mm:ss");
  };

  const renderResultCell = (data) => {
    const result = data.data.result;
    let className = "status-null";

    if (result === "approved") {
      className = "status-approved";
    } else if (result === "declined") {
      className = "status-declined";
    }

    return <div className={className}>{result || "NOT"}</div>;
  };

  const renderBooleanCell = (data) => {
    return data.value ? "Yes" : "No";
  };

  // Render statistics view
  if (navValue === 1) {
    console.log("Statistics data:", clientLogReport);

    return (
      <div className="statistics-container">
        {isLoading ? (
          <div className="loading-stats">Loading statistics...</div>
        ) : error ? (
          <div className="error-message">Error: {error}</div>
        ) : clientLogReport ? (
          <div className="statistics-card">
            <h2>Client Log Statistics</h2>
            <div className="statistics-item">
              <span className="stat-label">Total Logs:</span>
              <span className="stat-value">{clientLogReport.total}</span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">Not Clicked Rate:</span>
              <span className="stat-value">
                {clientLogReport.notClickedPercentage}%
              </span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">Card Entered Rate:</span>
              <span className="stat-value">
                {clientLogReport.cardEnteredPercentage}%
              </span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">Websocket Connected Rate:</span>
              <span className="stat-value">
                {clientLogReport.websocketConnectedPercentage}%
              </span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">Process Finished Rate:</span>
              <span className="stat-value">
                {clientLogReport.processFinishedPercentage}%
              </span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">Approved Rate:</span>
              <span className="stat-value">
                {clientLogReport.approvedPercentage}%
              </span>
            </div>
            <div className="statistics-item">
              <span className="stat-label">User Exited Rate:</span>
              <span className="stat-value">
                {clientLogReport.userExitedPercentage}%
              </span>
            </div>
          </div>
        ) : (
          <div className="error-message">No statistics data available</div>
        )}
      </div>
    );
  }

  // Render logs view (default)
  return (
    <div className="trader-logs-panel">
      <div className="search-container">
        <div className="search-row">
          <div className="search-field">
            <label>Start Date</label>
            <DateBox
              type="datetime"
              value={startDate}
              onValueChanged={(e) => setStartDate(e.value)}
              placeholder="Select Start Date"
            />
          </div>
          <div className="search-field">
            <label>End Date</label>
            <DateBox
              type="datetime"
              value={endDate}
              onValueChanged={(e) => setEndDate(e.value)}
              placeholder="Select End Date"
            />
          </div>
          <div className="search-buttons">
            <Button
              text="Clear"
              type="normal"
              onClick={handleClearSearch}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {error && <div className="error-message">Error: {error}</div>}

      <div className="logs-table">
        <DataGrid
          dataSource={filteredLogs}
          showBorders={false}
          showRowLines={false}
          hoverStateEnabled={true}
          rowAlternationEnabled={false}
          allowColumnResizing={true}
          columnAutoWidth={true}
          columnMinWidth={50}
          columnResizingMode="nextColumn"
          height="80vh"
        >
          <Selection mode="single" />
          <ColumnFixing enabled />
          <GroupPanel visible={false} />
          <SearchPanel visible={true} width={240} placeholder="Search" />
          <Paging defaultPageSize={100} />
          <Pager
            visible
            allowedPageSizes={[25, 50, 100]}
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
          <Scrolling
            useNative={false}
            scrollByContent
            scrollByThumb
            showScrollbar="onHover"
          />
          <Column dataField="transactionId" caption="Transaction ID" />
          <Column dataField="token" caption="Token" width={100} />
          <Column
            dataField="tokenCreationTime"
            caption="Token Creation Time"
            dataType="datetime"
            cellRender={(data) => formatDate(data.value)}
          />
          <Column
            dataField="isTokenConfirmed"
            caption="Token Confirmed"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="isUserClick"
            caption="User Click"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="isWebsocketConnected"
            caption="Websocket Connected"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="isUserEnterCardInfo"
            caption="Card Info Entered"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column dataField="bankName" caption="Bank Name" />
          <Column
            dataField="isUserEnterSMS"
            caption="SMS Entered"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="isUserExit"
            caption="User Exit"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="isProccessFinish"
            caption="Process Finished"
            dataType="boolean"
            cellRender={renderBooleanCell}
          />
          <Column
            dataField="result"
            caption="Result"
            cellRender={renderResultCell}
          />
          <Column
            dataField="createdAt"
            caption="Created At"
            dataType="datetime"
            cellRender={(data) => formatDate(data.value)}
          />
          <Column
            dataField="updatedAt"
            caption="Updated At"
            dataType="datetime"
            cellRender={(data) => formatDate(data.value)}
          />
        </DataGrid>
      </div>
    </div>
  );
};

export default TraderLogsPanel;
